import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import GalleryGrid from './components/GalleryGrid';
import Lightbox from './components/Lightbox';

interface GalleryImage {
  id?: string;
  src: string;
  category: string;
  alt: string;
}

export default function GalleryPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxImage, setLightboxImage] = useState<number | null>(null);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    const unsub = onSnapshot(doc(db, 'pages', 'gallery'), (doc) => {
      if (doc.exists() && doc.data().images) {
        // Map the CMS shape (url) to the frontend expected shape if needed, or just use url
        // In the Admin Panel, images are saved as { id, url, category, alt } 
        // Note: Earlier CMS seed used initialSeedImages without category/alt. We'll fallback gracefully.
        const dbImages = doc.data().images.map((img: any) => ({
          src: img.url || img.src,
          category: img.category || 'exterior',
          alt: img.alt || 'KARE YAPI İNŞAAT PROJESİ'
        }));
        setImages(dbImages);
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const categories = [
    { id: 'all', label: 'TÜMÜ' },
    { id: 'exterior', label: 'Dış Mekan' },
    { id: 'site', label: 'Site Projeleri' },
    { id: 'interior', label: 'İç Mekan' },
    { id: 'construction', label: 'Devam Eden Projeler' },
    { id: 'common', label: 'Ortak Alanlar' }
  ];

  const filteredImages = selectedCategory === 'all'
    ? images
    : images.filter(img => img.category === selectedCategory);

  const categoryCounts: Record<string, number> = {
    all: images.length,
    exterior: images.filter(i => i.category === 'exterior').length,
    interior: images.filter(i => i.category === 'interior').length,
    construction: images.filter(i => i.category === 'construction').length,
    common: images.filter(i => i.category === 'common').length,
    site: images.filter(i => i.category === 'site').length,
  };

  const handleImageClick = (index: number) => setLightboxImage(index);
  const handleCloseLightbox = () => setLightboxImage(null);

  const handlePrevImage = () => {
    if (lightboxImage !== null) {
      setLightboxImage(lightboxImage === 0 ? filteredImages.length - 1 : lightboxImage - 1);
    }
  };

  const handleNextImage = () => {
    if (lightboxImage !== null) {
      setLightboxImage(lightboxImage === filteredImages.length - 1 ? 0 : lightboxImage + 1);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <div className="relative h-80 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/tk-int-ref-1.png" 
            alt="KARE YAPI İNŞAAT Galeri" 
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center text-white px-4 flex flex-col items-center">
          <nav className="text-sm mb-4 font-medium">
            <a href="/" className="hover:text-red-400 transition-colors">Ana Sayfa</a>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-200">Galeri</span>
          </nav>
          <h1 className="text-5xl font-bold drop-shadow-lg tracking-tight" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Proje Galerisi
          </h1>
          <p className="mt-4 text-lg text-gray-200 drop-shadow max-w-xl">
            KARE YAPI İNŞAAT&apos;ın tüm güzelliklerini keşfedin
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="inline-flex items-center gap-2 bg-black/40 backdrop-blur-md text-white text-sm font-bold px-5 py-2.5 rounded-full border border-white/20 shadow-xl">
              <i className="ri-image-2-line"></i>
              {loading ? 'Yükleniyor...' : `${images.length} Proje`}
            </span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[136px] z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                    selectedCategory === category.id
                      ? 'bg-red-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-100 hover:scale-105 border border-gray-300'
                  }`}
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                >
                  {category.label}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold transition-colors duration-300 ${
                    selectedCategory === category.id ? 'bg-white text-red-600 shadow-sm' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {categoryCounts[category.id] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        {loading ? (
          <div className="flex justify-center items-center py-32">
             <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="text-center py-24 text-gray-400">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-50 rounded-full">
              <i className="ri-image-line text-4xl text-gray-300"></i>
            </div>
            <p className="text-lg font-medium text-gray-500">Bu kategoride henüz yayınlanmış bir proje bulunmuyor.</p>
          </div>
        ) : (
          <GalleryGrid
            images={filteredImages}
            onImageClick={handleImageClick}
          />
        )}
      </div>

      {/* Lightbox */}
      {lightboxImage !== null && (
        <Lightbox
          image={filteredImages[lightboxImage]}
          currentIndex={lightboxImage}
          totalImages={filteredImages.length}
          onClose={handleCloseLightbox}
          onPrev={handlePrevImage}
          onNext={handleNextImage}
        />
      )}

      <Footer />
    </div>
  );
}
